import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Cita, TipoServicio, ConfiguracionCalendario, EstadoCita, Empleado, Ubicacion } from '../components/calendario/types';
import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';
import { logger } from '../utils/logger';

interface CalendarioState {
  citas: Cita[];
  tiposServicio: TipoServicio[];
  empleados: Empleado[];
  ubicaciones: Ubicacion[];
  configuracion: ConfiguracionCalendario | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchCitas: (empresaId: string) => Promise<void>;
  fetchTiposServicio: (empresaId: string) => Promise<void>;
  fetchEmpleados: (empresaId: string) => Promise<void>;
  fetchUbicaciones: (empresaId: string) => Promise<void>;
  fetchConfiguracion: (empresaId: string) => Promise<void>;
  createCita: (cita: Omit<Cita, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Cita | null>;
  updateCita: (id: string, updates: Partial<Cita>) => Promise<void>;
  deleteCita: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useCalendarioStore = create<CalendarioState>((set, get) => ({
  citas: [],
  tiposServicio: [],
  empleados: [],
  ubicaciones: [],
  configuracion: null,
  loading: false,
  error: null,

  fetchCitas: async (empresaId: string) => {
    set({ loading: true, error: null });
    try {
      // Simplified: Just validate empresaId parameter
      if (!empresaId || empresaId === 'sin-empresa') {
        logger.debug('⚠️ CalendarioStore: No valid empresaId provided');
        set({ citas: [], loading: false });
        return;
      }
      
      logger.debug('📅 CalendarioStore: Fetching citas for empresa:', empresaId);
      
      // Get current user ID for filtering private events
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUserId = authStore.usuario?.id;

      // Fetch calendar events, facturas, and appointments to create comprehensive calendar view
      // Note: We'll filter private events after fetching
      const [calendarioResult, facturasResult, appointmentsResult] = await Promise.all([
        supabase
          .from('calendario')
          .select('*')
          .eq('empresa_id', empresaId)
          .order('fecha_inicio', { ascending: true }),
        supabase
          .from('facturas')
          .select(`
            *,
            clientes:cliente_id (
              id,
              nombre,
              apellido,
              telefono,
              email
            )
          `)
          .eq('empresa_id', empresaId)
          .not('vencimiento', 'is', null)
          .order('vencimiento', { ascending: true }),
        supabase
          .from('appointments')
          .select(`
            *,
            clientes:cliente_id (
              id,
              nombre,
              apellido,
              telefono,
              email
            )
          `)
          .eq('empresa_id', empresaId)
          .in('status', ['pending', 'confirmed'])
          .order('appointment_date', { ascending: true })
          .order('appointment_time', { ascending: true })
      ]);

      const calendarioEvents = calendarioResult.data || [];
      const facturas = facturasResult.data || [];
      const appointments = appointmentsResult.data || [];
      
      logger.debug('📅 Calendar events found:', calendarioEvents.length);
      logger.debug('📦 Facturas found:', facturas.length);
      logger.debug('📅 Appointments found:', appointments.length);

      // Check for errors in the API calls
      if (calendarioResult.error) {
        logger.error('❌ Error fetching calendar events:', calendarioResult.error);
      }
      if (facturasResult.error) {
        logger.error('❌ Error fetching facturas:', facturasResult.error);
      }
      if (appointmentsResult.error) {
        logger.error('❌ Error fetching appointments:', appointmentsResult.error);
      }

      // Filter out calendar events that are factura-related (to avoid duplicates)
      const _facturaEventIds = new Set(facturas.map(f => `factura-${f.id}`));
      
      // Filter events: exclude factura events and filter private events
      const nonFacturaEvents = calendarioEvents.filter(event => {
        // Exclude factura-related events
        if (event.titulo?.includes('Entrega:') || 
            event.titulo?.includes('Factura') ||
            event.tipo === 'FACTURA') {
          return false;
        }
        
        // Filter private events: only show if user created it or if it's public
        if (event.is_private === true) {
          // Only show if current user created it
          return event.created_by === currentUserId;
        }
        
        // Show all public events (is_private === false or null/undefined)
        return true;
      });
      
      logger.debug('📅 Non-factura calendar events:', nonFacturaEvents.length);
      logger.debug('🔍 Filtered out factura events:', calendarioEvents.length - nonFacturaEvents.length);
      
      // Convert calendar events to local format
      const citasFromCalendario = nonFacturaEvents.map((event: any) => ({
        id: event.id,
        empresaId: event.empresa_id,
        clienteId: event.cliente_id,
        cliente: event.cliente_id ? {
          id: event.cliente_id,
          empresaId: event.empresa_id,
          nombre: 'Cliente Asociado',
          apellido: '',
          telefono: 'N/A',
          email: 'N/A',
          numeroDocumento: 'N/A'
        } : undefined,
        tipoServicioId: event.tipo_servicio_id || '',
        empleadoId: event.empleado_id || '',
        ubicacionId: event.ubicacion_id || '',
        titulo: event.titulo,
        descripcion: event.descripcion,
        fecha: new Date(event.fecha_inicio),
        ordenDelDia: event.orden_del_dia || 0,
        estado: (event.estado as EstadoCita) || EstadoCita.PROGRAMADA,
        telefonoContacto: event.telefono_contacto || '',
        whatsapp: event.whatsapp || '',
        recordatorio24hEnviado: event.recordatorio_24h_enviado || false,
        recordatorio2hEnviado: event.recordatorio_2h_enviado || false,
        precio: event.precio,
        deposito: event.deposito,
        depositoPagado: event.deposito_pagado || false,
        notasInternas: event.notas_internas || '',
        tipoUbicacion: (event.tipo_ubicacion as 'TIENDA' | 'DOMICILIO' | 'VIRTUAL') || 'TIENDA',
        entregado: event.entregado || false,
        esRecurrente: event.es_recurrente || false,
        evidenciaEntregaPath: event.evidencia_entrega_path || null,
        createdAt: new Date(event.created_at || new Date()),
        updatedAt: new Date(event.updated_at || new Date()),
        // Add factura-specific fields
        esFactura: event.tipo === 'ENTREGA' || event.titulo?.includes('Entrega:'),
        facturaId: undefined,
        numeroFactura: undefined,
        tipoEntrega: undefined,
        direccionEntrega: undefined,
        horaEntrega: undefined,
        facturaItems: (() => {
          const items = event.items ? (typeof event.items === 'string' ? JSON.parse(event.items) : event.items) : [];
          // Map ItemFactura to calendar facturaItems structure
          return items.map((item: any) => ({
            id: item.id,
            producto: item.producto,
            descripcion: item.descripcion,
            cantidad: item.cantidad,
            precioUnitario: item.precioUnitario,
            subtotal: item.subtotal
          }));
        })()
      }));

      // Convert facturas to calendar events (only for unpaid facturas WITHOUT a linked cita)
      const citasFromFacturas = facturas
        .filter((factura: any) =>
          factura.estado_pago !== 'PAGADO' && // Only show unpaid facturas
          !factura.cita_id // Only create synthetic events for facturas without a linked calendar event
        )
        .map((factura: any) => {
        const cliente = factura.clientes || null;
        const _fechaEntrega = new Date(factura.vencimiento);
        
        // Create delivery time (default to 14:00 if not specified)
        const horaEntrega = factura.hora_agendada || '14:00';
        const timeParts = horaEntrega.split(':').map(Number);
        const hours = timeParts[0];
        const minutes = timeParts[1];
        const fechaConHora = new Date(factura.vencimiento);
        // Validate time parts before using
        if (hours !== undefined && minutes !== undefined) {
          fechaConHora.setHours(hours, minutes, 0, 0);
        }
        
        logger.debug('🕐 Delivery time calculation:', {
          vencimiento: factura.vencimiento,
          horaEntrega: horaEntrega,
          fechaConHora: fechaConHora.toISOString()
        });

        return {
          id: `factura-${factura.id}`,
          empresaId: factura.empresa_id,
          clienteId: factura.cliente_id,
          cliente: cliente ? {
            id: cliente.id,
            empresaId: factura.empresa_id,
            nombre: cliente.nombre || 'Cliente',
            apellido: cliente.apellido || '',
            telefono: cliente.telefono || 'N/A',
            email: cliente.email || 'N/A',
            numeroDocumento: 'N/A'
          } : undefined,
          tipoServicioId: '',
          empleadoId: '',
          ubicacionId: '',
          titulo: `📦 Entrega a domicilio: ${cliente?.nombre || factura.numero}`,
          descripcion: `Factura ${factura.numero} - ${cliente?.nombre || 'Cliente'}`,
          fecha: fechaConHora,
          ordenDelDia: 0,
          estado: (factura.estado_entrega === 'ENTREGADO' ? EstadoCita.COMPLETADA : EstadoCita.PROGRAMADA),
          telefonoContacto: cliente?.telefono || '',
          whatsapp: cliente?.telefono || '',
          recordatorio24hEnviado: false,
          recordatorio2hEnviado: false,
          precio: factura.total,
          deposito: 0,
          depositoPagado: factura.monto_pagado > 0,
          notasInternas: factura.notas || '',
          tipoUbicacion: (factura.tipo_entrega === 'DOMICILIO' ? 'DOMICILIO' : 'TIENDA') as 'TIENDA' | 'DOMICILIO' | 'VIRTUAL',
          entregado: factura.estado_entrega === 'ENTREGADO',
          esRecurrente: false,
          createdAt: new Date(factura.created_at || new Date()),
          updatedAt: new Date(factura.updated_at || new Date()),
          // Add factura-specific fields
          esFactura: true,
          facturaId: factura.id,
          numeroFactura: factura.numero,
          tipoEntrega: factura.tipo_entrega || 'TIENDA',
          direccionEntrega: factura.direccion_entrega || null,
          horaEntrega: horaEntrega,
          facturaItems: (() => {
            const items = factura.items ? (typeof factura.items === 'string' ? JSON.parse(factura.items) : factura.items) : [];
            
            // Map ItemFactura to calendar facturaItems structure
            return items.map((item: any) => ({
              id: item.id,
              producto: item.producto,
              descripcion: item.descripcion,
              cantidad: item.cantidad,
              precioUnitario: item.precioUnitario,
              subtotal: item.subtotal
            }));
          })()
        };
      });

      // Convert appointments to calendar events format
      const citasFromAppointments = appointments.map((appointment: any) => {
        const cliente = appointment.clientes || null;
        const appointmentDate = new Date(appointment.appointment_date);
        const [hours, minutes] = appointment.appointment_time.split(':').map(Number);
        const fechaCompleta = new Date(appointmentDate);
        fechaCompleta.setHours(hours || 0, minutes || 0, 0, 0);

        // Build title from items
        const itemsTitle = appointment.items && appointment.items.length > 0
          ? appointment.items.map((item: any) => item.name).join(', ')
          : 'Cita agendada';

        return {
          id: `appointment-${appointment.id}`,
          empresaId: appointment.empresa_id,
          clienteId: appointment.cliente_id,
          cliente: cliente ? {
            id: cliente.id,
            empresaId: appointment.empresa_id,
            nombre: cliente.nombre || 'Cliente',
            apellido: cliente.apellido || '',
            telefono: cliente.telefono || 'N/A',
            email: cliente.email || 'N/A',
            numeroDocumento: 'N/A'
          } : undefined,
          tipoServicioId: '',
          empleadoId: '',
          ubicacionId: '',
          titulo: `📅 ${itemsTitle}`,
          descripcion: `${appointment.fulfillment_type === 'delivery' ? 'Entrega a domicilio' : appointment.fulfillment_type === 'on-site' ? 'Servicio en sitio' : 'Recoger en tienda'}${appointment.notes ? ` - ${appointment.notes}` : ''}`,
          fecha: fechaCompleta,
          ordenDelDia: 0,
          estado: appointment.status === 'confirmed' ? EstadoCita.PROGRAMADA : EstadoCita.PROGRAMADA,
          telefonoContacto: cliente?.telefono || '',
          whatsapp: cliente?.telefono || '',
          recordatorio24hEnviado: false,
          recordatorio2hEnviado: false,
          precio: 0,
          deposito: 0,
          depositoPagado: false,
          notasInternas: appointment.notes || '',
          tipoUbicacion: (appointment.fulfillment_type === 'delivery' ? 'DOMICILIO' : appointment.fulfillment_type === 'on-site' ? 'DOMICILIO' : 'TIENDA') as 'TIENDA' | 'DOMICILIO' | 'VIRTUAL',
          entregado: appointment.status === 'completed',
          esRecurrente: false,
          createdAt: new Date(appointment.created_at || new Date()),
          updatedAt: new Date(appointment.updated_at || new Date()),
          esFactura: false,
          facturaId: undefined,
          numeroFactura: undefined,
          tipoEntrega: appointment.fulfillment_type || 'pickup',
          direccionEntrega: appointment.delivery_address || null,
          horaEntrega: appointment.appointment_time,
          facturaItems: appointment.items || []
        };
      });

      // Combine and sort all events by date
      const allCitas = [...citasFromCalendario, ...citasFromFacturas, ...citasFromAppointments].sort((a, b) => 
        a.fecha.getTime() - b.fecha.getTime()
      );

      set({ citas: allCitas, loading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Error al cargar citas',
        loading: false 
      });
    }
  },

  fetchTiposServicio: async (_empresaId: string) => {
    // TODO: Table doesn't exist yet - disable to prevent infinite loop
    logger.debug('fetchTiposServicio disabled - tipos_servicio table does not exist');
    set({ tiposServicio: [], loading: false });
  },

  fetchEmpleados: async (_empresaId: string) => {
    // TODO: Table doesn't exist yet - disable to prevent infinite loop
    logger.debug('fetchEmpleados disabled - empleados table does not exist');
    set({ empleados: [], loading: false });
  },

  fetchUbicaciones: async (_empresaId: string) => {
    // TODO: Table doesn't exist yet - disable to prevent infinite loop
    logger.debug('⚠️ fetchUbicaciones disabled - ubicaciones table does not exist');
    set({ ubicaciones: [], loading: false });
  },

  fetchConfiguracion: async (empresaId: string) => {
    set({ loading: true, error: null });
    try {
      // Get current user from auth store
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUser = authStore.usuario;
      
      if (!currentUser || !getCurrentEmpresaIdSync() || getCurrentEmpresaIdSync() === 'sin-empresa') {
        // Return null config for new users
        set({ configuracion: null, loading: false });
        return;
      }
      
      // Try to fetch from Supabase
      const { data, error } = await supabase
        .from('configuracion_calendario')
        .select('*')
        .eq('empresa_id', empresaId)
        .single();

      if (error) {
        logger.debug('Supabase error, returning null config:', error.message);
        set({ configuracion: null, loading: false });
        return;
      }

      set({ configuracion: data, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar configuración',
        loading: false
      });
    }
  },

  createCita: async (cita) => {
    set({ loading: true, error: null });
    try {
      logger.debug('🔄 Creating cita:', cita);
      
      const empresaId = getCurrentEmpresaIdSync();
      if (!empresaId || empresaId === 'sin-empresa') {
        throw new Error('Usuario no tiene empresa asignada');
      }
      
      logger.debug('👤 Creating cita for empresa:', empresaId);
      
      // Get current user ID for created_by
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUserId = authStore.usuario?.id;

      // Parse metadata from notasInternas if it's a JSON string
      let metadata: any = {};
      if (cita.notasInternas) {
        try {
          metadata = JSON.parse(cita.notasInternas);
        } catch {
          metadata = {};
        }
      }

      // Convert to Supabase format (calendario table). Omit created_at/updated_at so DB defaults apply.
      const supabaseEvent: any = {
        empresa_id: empresaId,
        cliente_id: cita.clienteId || null,
        titulo: cita.titulo || '',
        descripcion: cita.descripcion || '',
        fecha_inicio: cita.fecha.toISOString(),
        fecha_fin: new Date(cita.fecha.getTime() + 60 * 60 * 1000).toISOString(), // 1 hour duration
        tipo: cita.calendarTipo || 'CITA',
        estado: cita.estado || 'PROGRAMADA',
        color: '#10B981',
        empleado_id: cita.empleadoId || null,
        referencia_factura_id: cita.referenciaFacturaId || null,
      };

      if (cita.empleadoId) {
        supabaseEvent.asignado_a = cita.empleadoId;
      }
      if (cita.telefonoContacto) {
        supabaseEvent.telefono_contacto = cita.telefonoContacto;
      }
      if (cita.whatsapp) {
        supabaseEvent.whatsapp = cita.whatsapp;
      }
      if (cita.precio != null && cita.precio !== undefined) {
        supabaseEvent.precio = cita.precio;
      }
      if (cita.tipoUbicacion) {
        supabaseEvent.tipo_ubicacion = cita.tipoUbicacion;
      }
      if (cita.notasInternas) {
        try {
          JSON.parse(cita.notasInternas);
        } catch {
          supabaseEvent.notas_internas = cita.notasInternas;
        }
      }

      if (metadata.is_private !== undefined) {
        supabaseEvent.is_private = metadata.is_private;
      }
      if (metadata.created_by || currentUserId) {
        supabaseEvent.created_by = metadata.created_by || currentUserId;
      }
      if (metadata.visible_para_todos !== undefined) {
        supabaseEvent.visible_para_todos = metadata.visible_para_todos;
      }
      
      logger.debug('🔍 EVENT TO CREATE:', supabaseEvent);
      
      // Create in Supabase
      const { data: createdEvent, error: createError } = await supabase
        .from('calendario')
        .insert(supabaseEvent)
        .select()
        .maybeSingle();

      if (createError) {
        logger.error('🔍 SUPABASE CREATE ERROR DETAILS:', {
          message: createError.message,
          details: createError.details,
          hint: createError.hint,
          code: createError.code
        });
        throw createError;
      }
      if (!createdEvent) {
        throw new Error('No se devolvió el evento creado');
      }
      
      logger.debug('✅ Event creado en Supabase:', createdEvent);
      
      // Convert back to local format
      const nuevaCita: Cita = {
        id: createdEvent.id,
        empresaId: createdEvent.empresa_id,
        clienteId: createdEvent.cliente_id,
        tipoServicioId: createdEvent.tipo_servicio_id || '',
        empleadoId: createdEvent.empleado_id || '',
        ubicacionId: createdEvent.ubicacion_id || '',
        titulo: createdEvent.titulo,
        descripcion: createdEvent.descripcion,
        fecha: new Date(createdEvent.fecha_inicio),
        ordenDelDia: createdEvent.orden_del_dia || 0,
        estado: createdEvent.estado as EstadoCita,
        telefonoContacto: createdEvent.telefono_contacto || '',
        whatsapp: createdEvent.whatsapp || '',
        recordatorio24hEnviado: createdEvent.recordatorio_24h_enviado || false,
        recordatorio2hEnviado: createdEvent.recordatorio_2h_enviado || false,
        precio: createdEvent.precio,
        deposito: createdEvent.deposito,
        depositoPagado: createdEvent.deposito_pagado || false,
        notasInternas: createdEvent.notas_internas || '',
        tipoUbicacion: createdEvent.tipo_ubicacion || 'TIENDA',
        entregado: createdEvent.entregado || false,
        esRecurrente: createdEvent.es_recurrente || false,
        evidenciaEntregaPath: createdEvent.evidencia_entrega_path || null,
        createdAt: new Date(createdEvent.created_at || new Date()),
        updatedAt: new Date(createdEvent.updated_at || new Date())
      };
      
        set(state => ({
        citas: [nuevaCita, ...state.citas],
          loading: false
        }));

      logger.debug('✅ Cita creada exitosamente:', nuevaCita);
      return nuevaCita;
    } catch (error) {
      logger.error('❌ Error creating cita:', error);
      set({
        error: error instanceof Error ? error.message : 'Error al crear cita en Supabase',
        loading: false
      });
      return null;
    }
  },

  updateCita: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      logger.debug('🔄 Updating cita:', id, updates);

      if (id.startsWith('appointment-')) {
        const appointmentId = id.replace('appointment-', '');
        const apPatch: Record<string, unknown> = {};
        if (updates.estado !== undefined) {
          const statusByEstado: Record<string, string> = {
            [EstadoCita.PROGRAMADA]: 'confirmed',
            [EstadoCita.EN_PROCESO]: 'confirmed',
            [EstadoCita.COMPLETADA]: 'completed',
            [EstadoCita.ENTREGADA]: 'completed',
            [EstadoCita.CANCELADA]: 'cancelled',
            [EstadoCita.NO_ASISTIO]: 'no-show',
            [EstadoCita.REAGENDADA]: 'confirmed',
          };
          apPatch.status = statusByEstado[updates.estado] ?? 'confirmed';
        }
        if (updates.fecha !== undefined) {
          apPatch.appointment_date = updates.fecha.toISOString().split('T')[0];
          const h = updates.fecha.getHours();
          const mi = updates.fecha.getMinutes();
          apPatch.appointment_time = `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
        }
        if (updates.notasInternas !== undefined) apPatch.notes = updates.notasInternas;

        const { error: apErr } = await supabase
          .from('appointments')
          .update(apPatch)
          .eq('id', appointmentId);
        if (apErr) throw apErr;

        const updatedCitas = get().citas.map((c) =>
          c.id === id
            ? {
                ...c,
                ...(updates.estado !== undefined ? { estado: updates.estado } : {}),
                ...(updates.fecha !== undefined ? { fecha: updates.fecha } : {}),
                ...(updates.notasInternas !== undefined ? { notasInternas: updates.notasInternas } : {}),
                entregado:
                  updates.estado === EstadoCita.COMPLETADA ||
                  updates.estado === EstadoCita.ENTREGADA,
                updatedAt: new Date(),
              }
            : c
        );
        set({ citas: updatedCitas, loading: false });
        return;
      }

      if (id.startsWith('factura-')) {
        const facturaId = id.replace('factura-', '');
        if (updates.estado === EstadoCita.COMPLETADA || updates.estado === EstadoCita.ENTREGADA) {
          const { error: fErr } = await supabase
            .from('facturas')
            .update({
              estado_entrega: 'ENTREGADO',
              fecha_entrega_real: new Date().toISOString(),
            })
            .eq('id', facturaId);
          if (fErr) throw fErr;
        }

        const updatedCitas = get().citas.map((c) =>
          c.id === id
            ? {
                ...c,
                ...(updates.estado !== undefined ? { estado: updates.estado } : {}),
                ...(updates.fecha !== undefined ? { fecha: updates.fecha } : {}),
                entregado:
                  updates.estado === EstadoCita.COMPLETADA ||
                  updates.estado === EstadoCita.ENTREGADA ||
                  c.entregado,
                updatedAt: new Date(),
              }
            : c
        );
        set({ citas: updatedCitas, loading: false });
        return;
      }

      const supabaseUpdates: Record<string, unknown> = {};
      if (updates.titulo !== undefined) supabaseUpdates.titulo = updates.titulo;
      if (updates.descripcion !== undefined) supabaseUpdates.descripcion = updates.descripcion;
      if (updates.fecha !== undefined) {
        supabaseUpdates.fecha_inicio = updates.fecha.toISOString();
        supabaseUpdates.fecha_fin = new Date(updates.fecha.getTime() + 60 * 60 * 1000).toISOString();
      }
      if (updates.ordenDelDia !== undefined) supabaseUpdates.orden_del_dia = updates.ordenDelDia;
      if (updates.estado !== undefined) {
        supabaseUpdates.estado = updates.estado;
        switch (updates.estado) {
          case EstadoCita.EN_PROCESO:
            supabaseUpdates.estado_tarea = 'en_progreso';
            break;
          case EstadoCita.COMPLETADA:
          case EstadoCita.ENTREGADA:
            supabaseUpdates.estado_tarea = 'completado';
            break;
          case EstadoCita.PROGRAMADA:
          case EstadoCita.REAGENDADA:
            supabaseUpdates.estado_tarea = 'pendiente';
            break;
          case EstadoCita.CANCELADA:
          case EstadoCita.NO_ASISTIO:
            supabaseUpdates.estado_tarea = 'cancelado';
            break;
          default:
            break;
        }
      }
      if (updates.clienteId !== undefined) supabaseUpdates.cliente_id = updates.clienteId || null;
      if (updates.telefonoContacto !== undefined) supabaseUpdates.telefono_contacto = updates.telefonoContacto;
      if (updates.whatsapp !== undefined) supabaseUpdates.whatsapp = updates.whatsapp;
      if (updates.recordatorio24hEnviado !== undefined) {
        supabaseUpdates.recordatorio_24h_enviado = updates.recordatorio24hEnviado;
      }
      if (updates.recordatorio2hEnviado !== undefined) {
        supabaseUpdates.recordatorio_2h_enviado = updates.recordatorio2hEnviado;
      }
      if (updates.precio !== undefined) supabaseUpdates.precio = updates.precio;
      if (updates.deposito !== undefined) supabaseUpdates.deposito = updates.deposito;
      if (updates.depositoPagado !== undefined) supabaseUpdates.deposito_pagado = updates.depositoPagado;
      if (updates.notasInternas !== undefined) supabaseUpdates.notas_internas = updates.notasInternas;
      if (updates.tipoUbicacion !== undefined) supabaseUpdates.tipo_ubicacion = updates.tipoUbicacion;
      if (updates.empleadoId !== undefined) supabaseUpdates.empleado_id = updates.empleadoId || null;
      if (updates.entregado !== undefined) supabaseUpdates.entregado = updates.entregado;
      if (updates.esRecurrente !== undefined) supabaseUpdates.es_recurrente = updates.esRecurrente;
      if (updates.evidenciaEntregaPath !== undefined) {
        supabaseUpdates.evidencia_entrega_path = updates.evidenciaEntregaPath;
      }
      if (updates.referenciaFacturaId !== undefined) {
        supabaseUpdates.referencia_factura_id = updates.referenciaFacturaId || null;
      }
      if (updates.calendarTipo !== undefined) {
        supabaseUpdates.tipo = updates.calendarTipo;
      }
      if (updates.empleadoId !== undefined) {
        supabaseUpdates.asignado_a = updates.empleadoId || null;
      }

      supabaseUpdates.updated_at = new Date().toISOString();

      logger.debug('🔍 CITA UPDATES (calendario):', supabaseUpdates);

      const { data: updatedCita, error: updateError } = await supabase
        .from('calendario')
        .update(supabaseUpdates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        logger.error('🔍 SUPABASE UPDATE ERROR DETAILS:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code,
        });
        throw updateError;
      }

      logger.debug('✅ Cita actualizada en Supabase:', updatedCita);

      const citaLocal: Cita = {
        id: updatedCita.id,
        empresaId: updatedCita.empresa_id,
        clienteId: updatedCita.cliente_id,
        tipoServicioId: updatedCita.tipo_servicio_id,
        empleadoId: updatedCita.empleado_id,
        ubicacionId: updatedCita.ubicacion_id,
        titulo: updatedCita.titulo,
        descripcion: updatedCita.descripcion,
        fecha: new Date(updatedCita.fecha_inicio),
        ordenDelDia: updatedCita.orden_del_dia,
        estado: updatedCita.estado as EstadoCita,
        telefonoContacto: updatedCita.telefono_contacto,
        whatsapp: updatedCita.whatsapp,
        recordatorio24hEnviado: updatedCita.recordatorio_24h_enviado || false,
        recordatorio2hEnviado: updatedCita.recordatorio_2h_enviado || false,
        precio: updatedCita.precio,
        deposito: updatedCita.deposito,
        depositoPagado: updatedCita.deposito_pagado || false,
        notasInternas: updatedCita.notas_internas,
        tipoUbicacion: (updatedCita.tipo_ubicacion as Cita['tipoUbicacion']) || 'TIENDA',
        entregado: updatedCita.entregado || false,
        esRecurrente: updatedCita.es_recurrente || false,
        evidenciaEntregaPath: updatedCita.evidencia_entrega_path ?? null,
        createdAt: new Date(updatedCita.created_at),
        updatedAt: new Date(updatedCita.updated_at),
      };

      const prev = get().citas.find((c) => c.id === id);
      const merged: Cita = prev
        ? {
            ...prev,
            ...citaLocal,
            cliente: prev.cliente,
            esFactura: prev.esFactura,
            facturaId: prev.facturaId,
            facturaItems: prev.facturaItems,
          }
        : citaLocal;

      const updatedCitas = get().citas.map((c) => (c.id === id ? merged : c));

      set({
        citas: updatedCitas,
        loading: false,
      });

      logger.debug('✅ Cita actualizada exitosamente:', id);
    } catch (error) {
      logger.error('❌ Error updating cita:', error);
      const message = error instanceof Error ? error.message : 'Error al actualizar cita en Supabase';
      set({
        error: message,
        loading: false,
      });
      throw error instanceof Error ? error : new Error(message);
    }
  },

  deleteCita: async (id) => {
    set({ loading: true, error: null });
    try {
      logger.debug('🔄 Deleting cita:', id);

      // Check if this is a synthetic factura event (these can't be deleted, only hidden)
      if (id.startsWith('factura-')) {
        logger.debug('⚠️ Cannot delete synthetic factura event:', id);
        throw new Error('No se puede eliminar un evento de factura sintético. Para ocultar este evento, edite o elimine la factura.');
      }

      // Check if this is a synthetic appointment event
      if (id.startsWith('appointment-')) {
        const appointmentId = id.replace('appointment-', '');
        logger.debug('🔄 Deleting appointment:', appointmentId);
        
        // Delete from appointments table
        const { error: deleteError } = await supabase
          .from('appointments')
          .delete()
          .eq('id', appointmentId);
        
        if (deleteError) {
          logger.error('🔍 SUPABASE DELETE ERROR DETAILS:', {
            message: deleteError.message,
            details: deleteError.details,
            hint: deleteError.hint,
            code: deleteError.code
          });
          throw deleteError;
        }
        
        logger.debug('✅ Appointment eliminado de Supabase:', appointmentId);
        
        // Remove from local state
        const updatedCitas = get().citas.filter(c => c.id !== id);
        
        set(_state => ({
          citas: updatedCitas,
          loading: false
        }));
        
        logger.debug('✅ Appointment eliminado exitosamente:', appointmentId);
        return;
      }

      // Delete from Supabase calendario table
      const { error: deleteError } = await supabase
        .from('calendario')
        .delete()
        .eq('id', id);

      if (deleteError) {
        logger.error('🔍 SUPABASE DELETE ERROR DETAILS:', {
          message: deleteError.message,
          details: deleteError.details,
          hint: deleteError.hint,
          code: deleteError.code
        });
        throw deleteError;
      }

      logger.debug('✅ Cita eliminada de Supabase:', id);
      
      // Remove from local state
      const updatedCitas = get().citas.filter(c => c.id !== id);

      set(_state => ({
        citas: updatedCitas,
        loading: false
      }));

      logger.debug('✅ Cita eliminada exitosamente:', id);
    } catch (error) {
      logger.error('❌ Error deleting cita:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al eliminar cita en Supabase',
        loading: false 
      });
    }
  },

  clearError: () => {
    set({ error: null });
  }
}));
